"use client";

import { useEffect, useState } from "react";
import * as Sentry from "@sentry/nextjs";

function isSentryInitialized() {
  return Sentry.getClient() !== undefined;
}

function initializeAndVerifySentry({
  dsn,
  environment,
  release,
}: {
  dsn: string;
  environment: string;
  release?: string;
}) {
  // Check if Sentry is already initialized (prevents double initialization warning)
  if (isSentryInitialized()) {
    console.log(
      "[Sentry] Already initialized, skipping duplicate initialization",
    );
    return;
  }

  Sentry.init({
    ignoreErrors: [
      "Non-Error promise rejection captured",
      /^Unhandled Promise Rejection: ClerkJS: Token refresh failed/,
      /^Clerk: auth() was called but Clerk can't detect usage of clerkMiddleware()/,
    ], // https://github.com/getsentry/sentry-javascript/issues/3440#issuecomment-954602174
    dsn,
    environment,
    release,
    // Adjust this value in production, or use tracesSampler for greater control
    tracesSampleRate: 1,

    // Setting this option to true will print useful information to the console while you're setting up Sentry.
    debug: false,

    replaysOnErrorSampleRate: 1.0,

    // This sets the sample rate to be 10%. You may want this to be 100% while
    // in development and sample at a lower rate in production
    replaysSessionSampleRate: 0.1,
  });

  let sentryInitialized = false;

  while (!sentryInitialized) {
    sentryInitialized = isSentryInitialized();
  }

  // We need to wrap this in a try-catch block due to double rendering in development mode (React Strict Mode)
  // https://github.com/getsentry/sentry-javascript/discussions/8414
  try {
    Sentry.addIntegration(
      Sentry.replayIntegration({
        // Additional Replay configuration goes in here, for example:
        maskAllText: true,
        blockAllMedia: true,
      }),
    );
  } catch (error) {
    // ...
  }

  return true;
}

export type SentryConfig = {
  dsn?: string | undefined;
  environment: string;
  release?: string;
  userContext?: {
    id: string;
    email: string;
  };
};

export const SentryProvider = ({
  sentryConfig: { dsn, environment, release, userContext },
  children,
}: {
  sentryConfig: SentryConfig;
  children: React.ReactNode;
}) => {
  const shouldInitializeSentry = dsn !== undefined;
  // Start with false to prevent hydration mismatch - will be updated in useEffect
  const [isSentryReady, setIsSentryReady] = useState(false);

  useEffect(() => {
    const initializeSentry = () => {
      // Check if already initialized to prevent double initialization
      const alreadyInitialized = isSentryInitialized();

      if (shouldInitializeSentry && !alreadyInitialized) {
        initializeAndVerifySentry({ dsn: dsn!, environment, release });
      }

      if (userContext) {
        Sentry.setUser(userContext);
      }

      setIsSentryReady(true);
    };

    // Only run if not already ready to prevent repeated initialization
    if (!isSentryReady) {
      initializeSentry();
    }
  }, [isSentryReady, dsn, environment, userContext, shouldInitializeSentry]);

  if (!shouldInitializeSentry) {
    return <>{children}</>;
  }

  if (!isSentryReady) {
    return null;
  }

  return <>{children}</>;
};
